/**
 * TopBarToolsMenu — single overflow ⋮ menu that holds the secondary
 * topbar actions. Replaces the row of 6+ chips that made the header
 * look cluttered. Items inside are role-gated identically to the
 * inline chips they replace.
 *
 * Order (top to bottom):
 *   - Hot Leads (admin)
 *   - Product Classes (admin)
 *   - Admin Settings (admin)
 *   - Requested changes (admin)
 *   - Suggest a change (everyone)
 *   - Language picker (everyone)  ← reuses FloatingLanguagePicker's lang options
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretDown, Flame, Folders, ShieldCheck, Lightbulb, ChatTeardrop, Translate, ListChecks, MapPin, Eye } from "@phosphor-icons/react";
import { useT, useI18n } from "@/lib/i18n";
import NearbyCustomersModal from "@/components/NearbyCustomersModal";
import { useFieldTools } from "@/lib/useFieldTools";

const LANG_OPTIONS = ["en", "fr", "es"];

export default function TopBarToolsMenu({ user, onOpenAdmin, onOpenSuggest, pendingSuggestions = 0 }) {
  const navigate = useNavigate();
  const t = useT();
  const { lang, setLang } = useI18n();
  const { allowed: fieldAllowed, geocodingConfigured } = useFieldTools();
  const [open, setOpen] = useState(false);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const close = () => setOpen(false);
  const go = (path) => { close(); navigate(path); };
  const isAdmin = !!user?.is_admin;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid="topbar-tools-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("topbar.tools_tooltip", "More tools and shortcuts")}
        className="h-8 px-2 inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white text-slate-700 text-[10px] font-bold uppercase tracking-wider hover:bg-slate-50 hover:border-slate-300"
      >
        <span>{t("topbar.tools", "Tools")}</span>
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {pendingSuggestions > 0 && isAdmin && (
          <span
            data-testid="topbar-tools-pending-badge"
            className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full bg-amber-500 text-white"
          >
            {pendingSuggestions > 99 ? "99+" : pendingSuggestions}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid="topbar-tools-menu"
          className="absolute right-0 top-full mt-1.5 w-64 rounded-md border border-slate-200 bg-white shadow-lg z-[210] overflow-y-auto overscroll-contain max-h-[calc(100dvh-72px)] sm:max-h-[80vh]"
        >
          <div className="py-1">
            {isAdmin && (
              <>
                <MenuItem
                  testId="tools-menu-hot-leads"
                  icon={<Flame size={14} weight="fill" className="text-orange-500" />}
                  label={t("menu.hot_leads", "Hot Leads")}
                  hint={t("menu.hot_leads_desc", "Live email open tracking")}
                  onClick={() => go("/admin/hot-leads")}
                />
                <MenuItem
                  testId="tools-menu-product-classes"
                  icon={<Folders size={14} weight="bold" className="text-slate-500" />}
                  label={t("menu.product_classes", "Product Classes")}
                  hint={t("menu.product_classes_desc", "Product Classes catalog")}
                  onClick={() => go("/product-classes")}
                />
                <MenuItem
                  testId="tools-menu-admin-panel"
                  icon={<ShieldCheck size={14} weight="bold" className="text-slate-500" />}
                  label={t("menu.admin_settings", "Admin Settings")}
                  hint={t("menu.admin_settings_desc", "Org / users / billing")}
                  onClick={() => { close(); onOpenAdmin?.(); }}
                />
                <MenuItem
                  testId="tools-menu-requested-changes"
                  icon={<Lightbulb size={14} weight="fill" className="text-amber-600" />}
                  label={t("requested_changes.nav_label", "Requested changes")}
                  hint={t("requested_changes.nav_tooltip", "User-submitted improvement requests")}
                  badge={pendingSuggestions > 0 ? (pendingSuggestions > 99 ? "99+" : String(pendingSuggestions)) : null}
                  onClick={() => go("/admin/requested-changes")}
                />
                <Divider />
              </>
            )}
            <MenuItem
              testId="tools-menu-suggest-change"
              icon={<ChatTeardrop size={14} weight="fill" className="text-amber-700" />}
              label={t("suggest.button", "Suggest a change")}
              hint={t("suggest.tooltip", "Tell us how to improve the portal")}
              onClick={() => { close(); onOpenSuggest?.(); }}
            />
            <MenuItem
              testId="tools-menu-my-requests"
              icon={<ListChecks size={14} weight="fill" className="text-amber-700" />}
              label={t("my_requests.nav_label", "My requested changes")}
              hint={t("my_requests.nav_tooltip", "See status of every request you submitted")}
              onClick={() => go("/my-requests")}
            />
            <MenuItem
              testId="tools-menu-visibility-check"
              icon={<Eye size={14} weight="fill" className="text-blue-700" />}
              label={t("visibility.nav_label", "Why can I (not) see this compressor?")}
              hint={t("visibility.nav_hint", "Paste a serial and we'll explain exactly why")}
              onClick={() => go("/visibility-check")}
            />
            {fieldAllowed && (
              <>
                <Divider />
                <MenuItem
                  testId="tools-menu-nearby-customers"
                  icon={<MapPin size={14} weight="fill" className={geocodingConfigured ? "text-emerald-600" : "text-slate-400"} />}
                  label={t("field.nearby_label", "Find customers near me")}
                  hint={geocodingConfigured
                    ? t("field.nearby_hint", "Use GPS to find customers around you and start a recording")
                    : t("field.nearby_no_key", "No API Key — ask your super-admin to add the Google Maps key")}
                  title={geocodingConfigured ? undefined : t("field.nearby_no_key_short", "No API Key")}
                  badge={geocodingConfigured ? null : "No key"}
                  onClick={() => { close(); setNearbyOpen(true); }}
                />
              </>
            )}
            <Divider />
            <div className="px-3 py-2">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                <Translate size={11} weight="bold" />
                {t("topbar.language", "Language")}
              </div>
              <div className="flex flex-wrap gap-1" data-testid="tools-menu-lang-options">
                {(LANG_OPTIONS).map((code) => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); close(); }}
                    data-testid={`tools-menu-lang-${code}`}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${
                      lang === code
                        ? "bg-blue-700 border-blue-700 text-white"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nearby customers — gated by useFieldTools inside the component */}
      <NearbyCustomersModal open={nearbyOpen} onClose={() => setNearbyOpen(false)} />
    </div>
  );
}

function Divider() {
  return <div className="my-1 h-px bg-slate-100" />;
}

function MenuItem({ testId, icon, label, hint, badge, onClick, title }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      role="menuitem"
      title={title || undefined}
      className="w-full px-3 py-2 flex items-start gap-2 hover:bg-slate-50 text-left"
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-slate-900 truncate">{label}</span>
          {badge && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full bg-amber-500 text-white">
              {badge}
            </span>
          )}
        </span>
        {hint && <span className="block text-[10px] text-slate-500 mt-0.5">{hint}</span>}
      </span>
    </button>
  );
}
